import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Trash2, FileText, Users, Send, Plus, X, ShieldCheck, ShieldOff, CalendarPlus, Package, Mail, KeyRound } from "lucide-react";
import api, { apiError, fileUrl, passwordError } from "../../lib/api";
import { SetPasswordModal } from "./SetPasswordModal";

export default function CompanyDetail() {
  const { cid } = useParams();
  const nav = useNavigate();
  const [tab, setTab] = useState("reports");
  const [reports, setReports] = useState([]);
  const [users, setUsers] = useState([]);
  const [parts, setParts] = useState([]);
  const [recips, setRecips] = useState([]);
  const [show, setShow] = useState(false);
  const [pwUser, setPwUser] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "mechanic" });

  const load = useCallback(async () => {
    const [r, u, p, rc] = await Promise.all([
      api.get(`/admin/companies/${cid}/reports`),
      api.get(`/admin/companies/${cid}/users`),
      api.get(`/admin/companies/${cid}/parts`).catch(() => ({ data: [] })),
      api.get(`/admin/companies/${cid}/recipients`).catch(() => ({ data: [] })),
    ]);
    setReports(r.data); setUsers(u.data); setParts(p.data); setRecips(rc.data);
  }, [cid]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(apiError(e)); } };
  const access = (action, days) => act(() => api.put(`/admin/companies/${cid}/access`, { action, ...(days ? { days } : {}) }),
    action === "grant" ? "Access granted" : action === "revoke" ? "Access revoked" : "Trial extended");
  const inp = "bg-[#0F172A] border border-slate-700 text-white rounded-lg px-3 py-2 focus:border-amber-500 focus:outline-none";

  const createUser = async () => {
    if (!form.name || !form.email) return toast.error("Fill all fields");
    const pe = passwordError(form.password); if (pe) return toast.error(pe);
    try { await api.post(`/admin/companies/${cid}/users`, form); toast.success("User created"); setShow(false); setForm({ name: "", email: "", password: "", role: "mechanic" }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <button onClick={() => nav("/admin")} className="flex items-center gap-2 text-slate-400 text-sm mb-3"><ArrowLeft size={16} /> Companies</button>
      <h1 className="text-2xl font-extrabold text-white">Company detail</h1>

      <div className="bg-[#0F172A] border border-slate-800 rounded-xl p-4 mt-4" data-testid="company-access-panel">
        <p className="text-sm font-bold text-white flex items-center gap-2"><ShieldCheck size={16} className="text-amber-400" /> Subscription access</p>
        <p className="text-xs text-slate-400 mt-1">Comp, revoke or extend this company's trial. Grant gives permanent free access.</p>
        <div className="flex flex-wrap gap-2 mt-3">
          <button onClick={() => access("grant")} data-testid="company-access-grant" className="bg-emerald-500/15 border border-emerald-500/40 text-emerald-400 text-sm font-semibold px-3 py-2 rounded-lg flex items-center gap-1.5"><ShieldCheck size={15} /> Grant access</button>
          <button onClick={() => access("extend_trial", 7)} data-testid="company-access-extend" className="bg-amber-500/15 border border-amber-500/40 text-amber-400 text-sm font-semibold px-3 py-2 rounded-lg flex items-center gap-1.5"><CalendarPlus size={15} /> Extend trial +7d</button>
          <button onClick={() => window.confirm("Revoke this company's access? Their users will be blocked until they subscribe.") && access("revoke")} data-testid="company-access-revoke" className="bg-red-500/15 border border-red-500/40 text-red-400 text-sm font-semibold px-3 py-2 rounded-lg flex items-center gap-1.5"><ShieldOff size={15} /> Revoke</button>
        </div>
      </div>

      <div className="flex gap-2 mt-5 border-b border-slate-800">
        {["reports", "users", "parts", "recipients"].map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`company-tab-${t}`}
            className={`px-4 py-2 text-sm font-semibold capitalize flex items-center gap-1.5 border-b-2 ${tab === t ? "border-amber-500 text-amber-400" : "border-transparent text-slate-400"}`}>
            {t === "reports" ? <FileText size={16} /> : t === "users" ? <Users size={16} /> : t === "parts" ? <Package size={16} /> : <Mail size={16} />} {t}
          </button>
        ))}
      </div>

      {tab === "reports" && (
        <div className="mt-4 space-y-2">
          {reports.length === 0 && <p className="text-slate-500 text-sm py-6 text-center">No reports.</p>}
          {reports.map((r) => (
            <div key={r.id} data-testid={`admin-report-${r.id}`} className="flex items-center justify-between bg-[#0F172A] border border-slate-800 rounded-xl p-4">
              <div><p className="font-bold text-white">{r.customer_name}</p><p className="text-xs text-slate-400">{r.call_id} · {r.mechanic_name} · {r.report_date || (r.created_at || "").slice(0, 10)}</p></div>
              <div className="flex items-center gap-3">
                <a href={fileUrl(`reports/${r.id}/report.pdf`)} target="_blank" rel="noreferrer" className="text-sky-400 text-sm">PDF</a>
                <button onClick={() => act(() => api.post(`/admin/reports/${r.id}/resend`, {}), "Report re-sent")} className="text-emerald-400 flex items-center gap-1 text-sm" data-testid={`resend-${r.id}`}><Send size={15} /> Resend</button>
                <button onClick={() => window.confirm(`Delete report for ${r.customer_name}?`) && act(() => api.delete(`/admin/reports/${r.id}`), "Report deleted")} className="text-red-400" data-testid={`delete-report-${r.id}`}><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "users" && (
        <div className="mt-4">
          <button onClick={() => setShow(!show)} className="bg-amber-500 text-slate-900 font-bold px-3 py-2 rounded-lg text-sm flex items-center gap-1 mb-3" data-testid="admin-new-user-btn">{show ? <X size={16} /> : <Plus size={16} />} {show ? "Close" : "Add user"}</button>
          {show && (
            <div className="bg-[#0F172A] border border-slate-800 rounded-xl p-4 mb-3 grid grid-cols-1 md:grid-cols-2 gap-3">
              <input className={inp} placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <input className={inp} placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <input className={inp} type="password" placeholder="Password (10+)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
              <select className={inp} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>{["manager", "mechanic", "office"].map((r) => <option key={r} value={r}>{r}</option>)}</select>
              <button onClick={createUser} className="bg-amber-500 text-slate-900 font-bold rounded-lg py-2 md:col-span-2" data-testid="admin-create-user-btn">Create</button>
            </div>
          )}
          <div className="space-y-2">
            {users.map((u) => (
              <div key={u.id} className="flex items-center justify-between bg-[#0F172A] border border-slate-800 rounded-xl p-4">
                <div><p className="font-bold text-white">{u.name} <span className="text-xs text-amber-400/80">{u.role}</span></p><p className="text-xs text-slate-400">{u.email} · {u.mfa_enabled ? "2FA on" : "2FA not set"}</p></div>
                <div className="flex items-center gap-2">
                  <select value={u.role} onChange={(e) => act(() => api.put(`/admin/users/${u.id}/role`, { role: e.target.value }), "Role updated")} className="bg-[#0B1220] border border-slate-700 text-slate-200 text-xs rounded-lg px-2 py-1">
                    {["super_admin", "manager", "mechanic", "office"].map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                  <button onClick={() => setPwUser(u)} title="Set new password" className="text-amber-400" data-testid={`admin-set-password-${u.id}`}><KeyRound size={16} /></button>
                  <button onClick={() => window.confirm(`Delete ${u.name}?`) && act(() => api.delete(`/admin/users/${u.id}`), "User deleted")} className="text-red-400" data-testid={`admin-delete-user-${u.id}`}><Trash2 size={16} /></button>
                </div>
              </div>
            ))}
          </div>
          {pwUser && <SetPasswordModal user={pwUser} onClose={() => setPwUser(null)} />}
        </div>
      )}

      {tab === "parts" && (
        <div className="mt-4 space-y-2">
          {parts.length === 0 && <p className="text-slate-500 text-sm py-6 text-center">No parts.</p>}
          {parts.map((p) => (
            <div key={p.id} data-testid={`admin-part-${p.id}`} className="flex items-center justify-between bg-[#0F172A] border border-slate-800 rounded-xl p-4">
              <p className="font-bold text-white">{p.part_name}</p>
              <span className="text-xs text-amber-400 font-mono">{p.part_number}</span>
            </div>
          ))}
        </div>
      )}

      {tab === "recipients" && (
        <div className="mt-4 space-y-2">
          {recips.length === 0 && <p className="text-slate-500 text-sm py-6 text-center">No recipients.</p>}
          {recips.map((o) => (
            <div key={o.id} data-testid={`admin-recipient-${o.id}`} className="flex items-center justify-between bg-[#0F172A] border border-slate-800 rounded-xl p-4">
              <p className="font-bold text-white">{o.name}</p>
              <span className="text-xs text-slate-400">{o.email}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
